import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { getGridDateOperators, type GridFilterInputValueProps, type GridFilterItem, type GridFilterOperator } from '@mui/x-data-grid-pro'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { endOfDay, endOfMonth, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns'
import React, { useCallback } from 'react'

import { PatchedAdapterDateFns } from 'providers/PatchedAdapterDateFns'
import T from 'T'

// PatchedAdapterDateFns is untyped `.js`, so its formatTokenMap literals widen to
// `string` and don't satisfy FieldFormatTokenMap. It only adds maxLength to the year
// tokens, so the base class's type describes it. Drop the cast when it migrates to TS.
const DateAdapter = PatchedAdapterDateFns as unknown as typeof AdapterDateFns

export const RELATIVE_RANGE = {
  LAST_7_DAYS: 'LAST_7_DAYS',
  LAST_30_DAYS: 'LAST_30_DAYS',
  THIS_MONTH: 'THIS_MONTH',
  LAST_MONTH: 'LAST_MONTH',
} as const

export type RelativeRange = typeof RELATIVE_RANGE[keyof typeof RELATIVE_RANGE]

export const RELATIVE_RANGE_LABELS: Readonly<Record<RelativeRange, string>> = Object.freeze({
  [RELATIVE_RANGE.LAST_7_DAYS]: T.LAST_7_DAYS,
  [RELATIVE_RANGE.LAST_30_DAYS]: T.LAST_30_DAYS,
  [RELATIVE_RANGE.THIS_MONTH]: T.THIS_MONTH,
  [RELATIVE_RANGE.LAST_MONTH]: T.LAST_MONTH,
})

/** Resolved against "now" at filter time, so the window follows the clock. */
export const rangeFor = (range: RelativeRange): { start: Date; end: Date } => {
  const now = new Date()
  switch (range) {
    // Today counts as one of the days, so the window starts a day short of the label.
    case RELATIVE_RANGE.LAST_7_DAYS:
      return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) }
    case RELATIVE_RANGE.LAST_30_DAYS:
      return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) }
    case RELATIVE_RANGE.THIS_MONTH:
      return { start: startOfMonth(now), end: endOfDay(now) }
    default:
      return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) }
  }
}

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

/** Replaces the grid's native `<input type="date">` with the MUI picker. */
const DatePickerFilterInput = ({ item, applyValue }: GridFilterInputValueProps) => {
  const handleChange = useCallback((date: Date | null) => applyValue({ ...item, value: date ?? undefined }), [applyValue, item])

  return (
    <LocalizationProvider dateAdapter={DateAdapter}>
      <DatePicker
        label={T.RECEIVED}
        slotProps={{ textField: { variant: 'standard', size: 'small' } }}
        value={toDate(item.value)}
        onChange={handleChange}
      />
    </LocalizationProvider>
  )
}

const RelativeRangeFilterInput = ({ item, applyValue }: GridFilterInputValueProps) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => applyValue({ ...item, value: event.target.value }),
    [applyValue, item]
  )

  return (
    <TextField select label={T.PERIOD} size="small" value={(item.value as string) ?? ''} variant="standard" onChange={handleChange}>
      {(Object.keys(RELATIVE_RANGE_LABELS) as RelativeRange[]).map(range => (
        <MenuItem key={range} value={range}>
          {RELATIVE_RANGE_LABELS[range]}
        </MenuItem>
      ))}
    </TextField>
  )
}

const relativeRangeOperator: GridFilterOperator = {
  label: T.IS_WITHIN,
  value: 'isWithin',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    const range = filterItem.value as RelativeRange | undefined
    if (!range || !RELATIVE_RANGE_LABELS[range]) return null

    const { start, end } = rangeFor(range)
    return (value: unknown) => {
      const date = toDate(value)
      return date !== null && date >= start && date <= end
    }
  },
  InputComponent: RelativeRangeFilterInput,
}

/**
 * Date operators for the notification feed: the grid's own comparisons but driven
 * by the MUI picker, plus a relative window so "last 30 days" doesn't require
 * picking a date at all.
 */
export const notificationDateOperators: GridFilterOperator[] = [
  ...getGridDateOperators().map(operator => ({ ...operator, InputComponent: DatePickerFilterInput })),
  relativeRangeOperator,
]
